/**
 * t281u: how an open shift fits one person's own schedule. The checks are the managers' rota engine (the server runs
 * evaluateWorkingTime with contract checks, availability, leave and skills for the person as if they had the shift);
 * this file only sorts those warnings into:
 *  - hidden: shifts they can't do at all (already working then at any building, on approved leave, marked unavailable).
 *    The list leaves them out and a claim is refused with `openShiftBlockedMessage`.
 *  - notes: working rules the shift would break, each as a short plain en-GB note ("Takes you to 46h this week",
 *    "Only 9h rest after your Tue shift"). A claim with any note always goes to a manager, who sees the same notes.
 * Shift-level checks that don't depend on the person (closure days, clock changes, an adult rest break the manager
 * didn't schedule, a position no longer allowed at the building) are the manager's to fix and are ignored here.
 */
import { DateTime } from 'luxon';
import type { OpenShiftClaimWarning, WtrWarning } from './types';
import { DEFAULT_TZ, formatMinutes, toLocalDate, addDays } from './time';

export interface FitShift { id?: string; starts_at: string; ends_at: string }
export interface FitOptions {
  /** The open shift being looked at. */
  shift: FitShift;
  /** The person's own (published) shifts, to name the day of the shift a rest note is about. */
  mine?: readonly FitShift[];
  zone?: string;
  /** 'you' for the person, 'them' for the approver. */
  who?: 'you' | 'them';
}
export interface OpenShiftFit { hidden: WtrWarning[]; notes: OpenShiftClaimWarning[] }

/** Can't do it at all: hidden from the list and refused on claim. */
export const blocksOpenShift = (w: Pick<WtrWarning, 'code' | 'severity'>) =>
  w.code === 'overlap' || w.code === 'unavailable' || (w.code === 'on_leave' && w.severity === 'error');

const NOTE_ORDER = ['hours', 'daily_rest', 'weekly_rest', 'contract_days', 'average_48h', 'young_daily_limit', 'young_night_work', 'rest_break', 'on_leave', 'missing_skill'] as const;
const HOURS = new Set(['weekly_hours', 'contract_hours', 'max_hours_preference', 'young_weekly_limit']);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
/** 46h, 37.5h; other part hours as "9h 45m". */
const hoursText = (min: number) => (min % 30 === 0 ? `${min / 60}h` : formatMinutes(min));

/** The run of consecutive working days (local dates) that includes `date`. */
export function consecutiveDays(dates: Iterable<string>, date: string): number {
  const set = new Set(dates);
  set.add(date);
  let n = 1;
  for (let d = addDays(date, -1); set.has(d); d = addDays(d, -1)) n++;
  for (let d = addDays(date, 1); set.has(d); d = addDays(d, 1)) n++;
  return n;
}

/** One short note for one warning, or null when the warning isn't about this person's own schedule. */
export function openShiftNoteText(w: WtrWarning, o: FitOptions): string | null {
  const zone = o.zone ?? DEFAULT_TZ;
  const you = (o.who ?? 'you') === 'you';
  const Y = you ? 'you' : 'them', your = you ? 'your' : 'their', Your = you ? 'Your' : 'Their', youve = you ? "You've" : "They've";
  const d = w.details ?? {};
  const day = (id: unknown) => {
    const s = o.mine?.find(m => m.id === id);
    return s ? DateTime.fromISO(s.starts_at, { zone }).setLocale('en-GB').toFormat('ccc') : null;
  };
  switch (w.code) {
    case 'weekly_hours': case 'contract_hours': case 'max_hours_preference': case 'young_weekly_limit': {
      const m = num(d.minutes);
      const to = m != null ? `Takes ${Y} to ${hoursText(m)} this week` : `Takes ${Y} over ${your} weekly hours`;
      if (w.code === 'contract_hours') return num(d.contracted_hours) != null ? `${to} (contract ${d.contracted_hours}h)` : to;
      if (w.code === 'max_hours_preference') return num(d.max_hours) != null ? `${to} (${your} max is ${d.max_hours}h)` : to;
      if (w.code === 'young_weekly_limit') return `${to} (under-18 limit 40h)`;
      return `${to} (over 48h)`;
    }
    case 'daily_rest': {
      const rest = num(d.rest_minutes);
      const r = rest != null ? `Only ${hoursText(rest)} rest` : 'Not enough rest';
      const focusIsLater = !w.shift_id || w.shift_id === o.shift.id;
      if (focusIsLater) { const p = day(d.previous_shift_id); return p ? `${r} after ${your} ${p} shift` : `${r} after ${your} last shift`; }
      const n = day(w.shift_id); return n ? `${r} before ${your} ${n} shift` : `${r} before ${your} next shift`;
    }
    case 'weekly_rest': {
      const date = toLocalDate(o.shift.starts_at, zone);
      const run = consecutiveDays((o.mine ?? []).map(s => toLocalDate(s.starts_at, zone)), date);
      return run >= 7 ? `Makes ${run} days in a row` : 'No full day off that week';
    }
    case 'contract_days': {
      const n = num(d.days), max = num(d.max_days);
      return n != null && max != null ? `${n} days this week (contract ${max})` : `More days than ${your} contract this week`;
    }
    case 'average_48h': return `Takes ${your} average over 48h a week`;
    case 'young_daily_limit': return `Over 8h in a day (under-18 limit)`;
    case 'young_night_work': return 'Night work (under-18)';
    case 'rest_break': return num(d.required_minutes) === 30 ? 'Needs a 30-minute break (under-18)' : null;
    case 'on_leave': return w.severity === 'error' ? null : `${youve} asked for ${(str(d.leave_name) ?? 'time off').toLowerCase()} that day`;
    case 'missing_skill': {
      const skill = str(d.skill_name) ?? 'a required skill';
      return d.expired_on ? `${Your} ${skill} has expired` : `Needs ${skill}`;
    }
    default: return null;
  }
}

/** Sorts one open shift's warnings for one person into hidden and notes (one note per kind, most important first). */
export function openShiftFit(warnings: readonly WtrWarning[], o: FitOptions): OpenShiftFit {
  const hidden = warnings.filter(blocksOpenShift);
  const seen = new Map<string, OpenShiftClaimWarning>();
  for (const w of warnings) {
    if (blocksOpenShift(w)) continue;
    const text = openShiftNoteText(w, o);
    if (!text) continue;
    const key = HOURS.has(w.code) ? 'hours' : w.code === 'daily_rest' ? `daily_rest:${text}` : w.code === 'missing_skill' ? `skill:${text}` : w.code;
    const prev = seen.get(key);
    // Several hours rules at once: the contract or personal maximum explains it best, then the legal limits.
    const rank = (c: string) => ['contract_hours', 'max_hours_preference', 'young_weekly_limit', 'weekly_hours'].indexOf(c);
    if (prev && !(HOURS.has(w.code) && rank(w.code) < rank(prev.code))) continue;
    seen.set(key, { code: w.code, severity: w.severity, text });
  }
  const order = (n: OpenShiftClaimWarning) => NOTE_ORDER.indexOf((HOURS.has(n.code) ? 'hours' : n.code) as (typeof NOTE_ORDER)[number]);
  return { hidden, notes: [...seen.values()].sort((a, b) => order(a) - order(b)) };
}

/** The friendly refusal when someone claims a shift they can't do. */
export function openShiftBlockedMessage(hidden: readonly WtrWarning[]): string {
  const w = hidden.find(h => h.code === 'overlap') ?? hidden.find(h => h.code === 'on_leave') ?? hidden[0];
  if (!w) return "This open shift isn't available to you";
  if (w.code === 'overlap') return "You're already working then, so you can't pick up this shift";
  if (w.code === 'on_leave') return `You're on ${(str(w.details?.leave_name) ?? 'time off').toLowerCase()} that day, so you can't pick up this shift`;
  return "You've marked yourself unavailable then, so you can't pick up this shift";
}

/** One muted line for a list row: the notes joined with " · ". */
export const openShiftNotesLine = (notes: readonly Pick<OpenShiftClaimWarning, 'text'>[] | null | undefined) => (notes ?? []).map(n => n.text).join(' · ');

/**
 * t346u: the muted line on the staff open-shift list (My shifts → Open). Leaves out what a shift requires ("Needs Forklift
 * licence"); personal notes (rest, hours, time off, an expired skill of yours) stay. Eligibility and the claim checks are
 * unchanged, and a refused claim still says why; managers' claim lists keep the full line.
 */
export const openShiftPickupLine = (notes: readonly Pick<OpenShiftClaimWarning, 'code' | 'text'>[] | null | undefined) =>
  openShiftNotesLine((notes ?? []).filter(n => !(n.code === 'missing_skill' && n.text.startsWith('Needs '))));

/** t347u: open shifts I could still claim (not already asked for), as counted on the Open tab label. */
export const claimableOpenShifts = <S extends { my_claim_status?: string | null }>(shifts: readonly S[] | null | undefined) => (shifts ?? []).filter(s => !s.my_claim_status);

/** The toast after a claim: straight to you, or waiting on a manager (always the case when the shift had a note). */
export const claimResultText = (r: { status: string; warnings?: readonly unknown[] }) =>
  r.status === 'approved' ? "Claimed - it's yours"
    : r.status === 'pending' ? (r.warnings?.length ? 'Sent to your manager to check' : 'Sent to your manager to approve')
      : r.status === 'rejected' ? 'Your earlier request for this shift was declined' : 'Claimed';
